// Which sources one Ask Prompta question is answered from.
//
// The chat draws on everything in the project by default, and a selection
// narrows a single turn. These helpers are the shared vocabulary between the
// control that renders the checkboxes (`ChatSettingsButton`, whose popover
// holds Sources as a section under AI model) and the two chats that send the
// request — the single-report one (`Insight/AskPrompta`) and the project-wide
// one (`Projects/CompareResultsPage`'s `CompareInsightChat`).
//
// `available` is the `rag-sources` endpoint's payload — the sources the project
// actually HAS. A selection of `null` means "everything", which is the state a
// fresh conversation starts in and the state whose request must stay
// byte-identical to the pre-feature one (see `buildSourcesPayload`).
//
// Risk is deliberately absent: the endpoint reports `risk_model`, but the chat
// has no risk grounding for a flag to gate yet, so v1 offers no risk option.

const waveIdsOf = (available) => (available?.waves || []).map((wave) => wave.id);
const documentIdsOf = (available) => (available?.documents || []).map((doc) => doc.id);

// The explicit "everything" selection, which is what `null` stands for.
export const allOf = (available) => ({
    waveIds: waveIdsOf(available),
    documentIds: documentIdsOf(available),
    includeImpacts: Boolean(available?.impact_assessment),
});

// A selection as explicit lists — `null` expanded to everything available.
export const resolveSelection = (value, available) => {
    if (!value) return allOf(available);
    // Intersected with what is available, so a stale id (a document deleted
    // since the options were fetched) can never reach the wire.
    const waves = new Set(waveIdsOf(available));
    const documents = new Set(documentIdsOf(available));
    return {
        waveIds: (value.waveIds || []).filter((id) => waves.has(id)),
        documentIds: (value.documentIds || []).filter((id) => documents.has(id)),
        includeImpacts: Boolean(available?.impact_assessment) && Boolean(value.includeImpacts),
    };
};

// How many sources the selection covers. Zero is the state a caller refuses to
// send in — there would be nothing to answer from.
export const selectedCount = (value, available) => {
    const selection = resolveSelection(value, available);
    return (
        selection.waveIds.length +
        selection.documentIds.length +
        (selection.includeImpacts ? 1 : 0)
    );
};

// Whether there is anything to offer. A project with no waves, no documents
// and no impact register gets no Sources section at all.
export const hasSources = (available) =>
    waveIdsOf(available).length > 0 ||
    documentIdsOf(available).length > 0 ||
    Boolean(available?.impact_assessment);

// Toggle one id in a selected list.
export const toggleId = (ids, id) =>
    ids.includes(id) ? ids.filter((each) => each !== id) : [...ids, id];

// The selection as the chat request's `sources` field, or `undefined`.
//
// `undefined` when everything available is selected is the whole point: the
// default request then carries no `sources` key and is byte-identical to the
// one this feature did not exist for. The backend reads an absent `sources` as
// "no opinion" and takes its pre-feature path (`rag.services.source_filter`).
//
// In a narrowed selection a group that is still whole is OMITTED rather than
// listed, because a missing list means "all of them" there while an empty list
// means "none of them" — the two are not interchangeable.
export const buildSourcesPayload = (value, available) => {
    if (!available) return undefined;

    const selection = resolveSelection(value, available);
    const allWaves = selection.waveIds.length === waveIdsOf(available).length;
    const allDocuments = selection.documentIds.length === documentIdsOf(available).length;
    // A project with no register has nothing to include, so it is never what
    // holds the selection back from being "everything".
    const allImpacts = !available.impact_assessment || selection.includeImpacts;

    if (allWaves && allDocuments && allImpacts) return undefined;

    const payload = {
        // False for a project with no register too — behaviourally identical
        // (there is nothing to fetch either way), and it keeps the backend's
        // zero-source guard agreeing with the Send button the chats disable.
        include_impacts: selection.includeImpacts,
    };
    if (!allWaves) payload.wave_report_ids = selection.waveIds;
    if (!allDocuments) payload.document_ids = selection.documentIds;
    return payload;
};
